import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Barcode, Sparkles } from 'lucide-react'
import {
  createProductSchema, formatMoney, PRODUCT_TYPES, productSchema, profitMargin, TAX_RATES, toMajor, toMinor, UNITS,
  type ProductType,
} from '@najah/core'
import {
  Alert, Button, Dialog, Input, Select, Switch, Textarea, useToast,
} from '@najah/ui'
import { categoriesApi, flattenCategories, lookupsApi, productsApi, type ProductDetail } from '../lib/catalogApi'
import { ImageUploader } from './ImageUploader'
import { useFormErrors } from '../lib/useFormErrors'

interface Props {
  open: boolean
  onClose: () => void
  /** موجود = تعديل، غائب = إنشاء */
  product?: ProductDetail | null
}

/** الحالة الداخلية للنموذج: الأسعار بالدينار (لا بالسنتيم) لأن المستخدم يكتب دنانير */
interface FormState {
  sku: string
  barcode: string
  nameAr: string
  nameFr: string
  description: string
  type: ProductType
  categoryId: string
  unit: string
  costPrice: string
  retailPrice: string
  wholesalePrice: string
  taxRate: string
  minStock: string
  trackStock: boolean
  isActive: boolean
  openingQty: string
  isbn: string
  authorId: string
  publisherId: string
  pages: string
  language: string
  edition: string
  publishYear: string
  images: string[]
}

const EMPTY: FormState = {
  sku: '', barcode: '', nameAr: '', nameFr: '', description: '',
  type: 'STATIONERY', categoryId: '', unit: 'قطعة',
  costPrice: '', retailPrice: '', wholesalePrice: '', taxRate: '0',
  minStock: '0', trackStock: true, isActive: true, openingQty: '0',
  isbn: '', authorId: '', publisherId: '', pages: '', language: 'العربية', edition: '', publishYear: '',
  images: [],
}

function toForm(p: ProductDetail): FormState {
  return {
    sku: p.sku,
    barcode: p.barcode ?? '',
    nameAr: p.nameAr,
    nameFr: p.nameFr ?? '',
    description: p.description ?? '',
    type: p.type,
    categoryId: p.categoryId ?? '',
    unit: p.unit,
    costPrice: String(toMajor(p.costPrice)),
    retailPrice: String(toMajor(p.retailPrice)),
    wholesalePrice: p.wholesalePrice != null ? String(toMajor(p.wholesalePrice)) : '',
    taxRate: String(p.taxRate),
    minStock: String(p.minStock),
    trackStock: p.trackStock,
    isActive: p.isActive,
    openingQty: '0',
    isbn: p.bookDetail?.isbn ?? '',
    authorId: p.bookDetail?.authorId ?? '',
    publisherId: p.bookDetail?.publisherId ?? '',
    pages: p.bookDetail?.pages != null ? String(p.bookDetail.pages) : '',
    language: p.bookDetail?.language ?? 'العربية',
    edition: p.bookDetail?.edition ?? '',
    publishYear: p.bookDetail?.publishYear != null ? String(p.bookDetail.publishYear) : '',
    images: p.images ?? [],
  }
}

export function ProductFormDialog({ open, onClose, product }: Props) {
  const isEdit = !!product
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()
  const [form, setForm] = useState<FormState>(EMPTY)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  useEffect(() => {
    if (!open) return
    clear()
    setForm(product ? toForm(product) : EMPTY)
  }, [open, product, clear])

  const { data: categoryTree } = useQuery({
    queryKey: ['categories', 'tree'],
    queryFn: () => categoriesApi.tree(true),
    enabled: open,
  })
  const { data: authors } = useQuery({ queryKey: ['authors'], queryFn: lookupsApi.authors, enabled: open })
  const { data: publishers } = useQuery({ queryKey: ['publishers'], queryFn: lookupsApi.publishers, enabled: open })

  const categoryOptions = useMemo(() => (categoryTree ? flattenCategories(categoryTree) : []), [categoryTree])

  const isBook = form.type === 'BOOK'
  const isService = form.type === 'SERVICE'

  // الخدمة لا تُخزَّن — نُطفئ التتبّع تلقائياً بدل ترك المستخدم يصطدم بخطأ تحقّق
  useEffect(() => {
    if (isService && form.trackStock) set('trackStock', false)
  }, [isService, form.trackStock])

  const margin = useMemo(() => {
    const cost = Number(form.costPrice)
    const retail = Number(form.retailPrice)
    if (!cost || !retail) return null
    return profitMargin(toMinor(cost), toMinor(retail))
  }, [form.costPrice, form.retailPrice])

  function buildPayload() {
    const bookDetail = isBook
      ? {
          isbn: form.isbn || null,
          authorId: form.authorId || null,
          publisherId: form.publisherId || null,
          pages: form.pages ? Number(form.pages) : null,
          language: form.language || null,
          edition: form.edition || null,
          publishYear: form.publishYear ? Number(form.publishYear) : null,
        }
      : null

    return {
      sku: form.sku.trim(),
      barcode: form.barcode.trim() || null,
      nameAr: form.nameAr.trim(),
      nameFr: form.nameFr.trim() || null,
      description: form.description.trim() || null,
      type: form.type,
      categoryId: form.categoryId || null,
      unit: form.unit,
      // المستخدم يكتب دنانير؛ الخادم يتعامل بالسنتيم حصراً
      costPrice: toMinor(Number(form.costPrice) || 0),
      retailPrice: toMinor(Number(form.retailPrice) || 0),
      wholesalePrice: form.wholesalePrice ? toMinor(Number(form.wholesalePrice)) : null,
      taxRate: Number(form.taxRate) || 0,
      minStock: Number(form.minStock) || 0,
      trackStock: form.trackStock,
      isActive: form.isActive,
      images: form.images,
      bookDetail,
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const base = buildPayload()

      if (isEdit) {
        const parsed = validate(productSchema, base)
        if (!parsed) throw new Error('VALIDATION')
        return productsApi.update(product.id, parsed)
      }

      const parsed = validate(createProductSchema, { ...base, openingQty: Number(form.openingQty) || 0 })
      if (!parsed) throw new Error('VALIDATION')
      return productsApi.create(parsed)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success(isEdit ? 'تم حفظ التعديلات' : 'تمت إضافة المنتج', saved.nameAr)
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ المنتج')
    },
  })

  const suggestSku = useMutation({
    mutationFn: () => productsApi.suggestSku(form.type),
    onSuccess: ({ sku }) => set('sku', sku),
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title={isEdit ? `تعديل: ${product.nameAr}` : 'إضافة منتج جديد'}
      description={isEdit ? `الرمز ${product.sku}` : 'املأ بيانات المنتج — الحقول المعلَّمة بـ * مطلوبة'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            {isEdit ? 'حفظ التعديلات' : 'إضافة المنتج'}
          </Button>
        </>
      }
    >
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); mutation.mutate() }}>
        {formError && <Alert tone="danger">{formError}</Alert>}

        {/* ── الأساسيات ── */}
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-fg-muted">البيانات الأساسية</h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="اسم المنتج (عربي)" required
              value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)}
              error={errors.nameAr} autoFocus
            />
            <Input
              label="الاسم بالفرنسية" ltr
              value={form.nameFr} onChange={(e) => set('nameFr', e.target.value)}
              error={errors.nameFr}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Select
              label="النوع" required
              value={form.type} onChange={(e) => set('type', e.target.value as ProductType)}
              options={Object.entries(PRODUCT_TYPES).map(([value, label]) => ({ value, label }))}
              error={errors.type}
            />
            <Select
              label="التصنيف"
              value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}
              options={categoryOptions}
              placeholder="بلا تصنيف"
              error={errors.categoryId}
            />
            <Select
              label="وحدة البيع"
              value={form.unit} onChange={(e) => set('unit', e.target.value)}
              options={UNITS.map((u) => ({ value: u, label: u }))}
              error={errors.unit}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-end gap-2">
              <Input
                label="الرمز (SKU)" required ltr
                value={form.sku} onChange={(e) => set('sku', e.target.value)}
                error={errors.sku}
                hint="يظهر على ملصق المنتج"
              />
              <Button
                type="button" variant="secondary" size="md"
                onClick={() => suggestSku.mutate()} loading={suggestSku.isPending}
                title="اقتراح رمز فريد"
                className="mb-[1.375rem] shrink-0"
              >
                <Sparkles className="size-4" />
              </Button>
            </div>

            <Input
              label="الباركود" ltr
              value={form.barcode} onChange={(e) => set('barcode', e.target.value)}
              error={errors.barcode}
              startIcon={<Barcode className="size-4" />}
              hint="امسحه بالقارئ أو اتركه فارغاً"
            />
          </div>

          <Textarea
            label="الوصف"
            value={form.description} onChange={(e) => set('description', e.target.value)}
            error={errors.description}
          />
        </section>

        {/* ── التسعير ── */}
        <section className="space-y-4 border-t border-border pt-5">
          <h3 className="text-sm font-semibold text-fg-muted">التسعير</h3>

          <div className="grid gap-4 sm:grid-cols-4">
            <Input
              label="سعر التكلفة" type="number" min="0" step="0.01" ltr
              value={form.costPrice} onChange={(e) => set('costPrice', e.target.value)}
              error={errors.costPrice} hint="دج"
            />
            <Input
              label="سعر البيع" required type="number" min="0" step="0.01" ltr
              value={form.retailPrice} onChange={(e) => set('retailPrice', e.target.value)}
              error={errors.retailPrice} hint="دج"
            />
            <Input
              label="سعر الجملة" type="number" min="0" step="0.01" ltr
              value={form.wholesalePrice} onChange={(e) => set('wholesalePrice', e.target.value)}
              error={errors.wholesalePrice} hint="اختياري"
            />
            <Select
              label="الضريبة"
              value={form.taxRate} onChange={(e) => set('taxRate', e.target.value)}
              options={TAX_RATES.map((r) => ({ value: String(r), label: r === 0 ? 'معفى' : `${r}%` }))}
              error={errors.taxRate}
            />
          </div>

          {margin !== null && (
            <p className="text-xs text-fg-muted">
              هامش الربح:{' '}
              <span className={margin < 0 ? 'font-semibold text-danger' : 'font-semibold text-success'} dir="ltr">
                {margin.toFixed(1)}%
              </span>
              {' · '}
              الربح للوحدة:{' '}
              <span dir="ltr">{formatMoney(toMinor(Number(form.retailPrice)) - toMinor(Number(form.costPrice)))}</span>
            </p>
          )}
        </section>

        {/* ── المخزون ── */}
        <section className="space-y-4 border-t border-border pt-5">
          <h3 className="text-sm font-semibold text-fg-muted">المخزون</h3>

          <Switch
            checked={form.trackStock}
            onChange={(e) => set('trackStock', e.target.checked)}
            disabled={isService}
            label="تتبّع المخزون"
            description={isService ? 'الخدمات لا مخزون لها' : 'أوقفه للمنتجات غير المخزَّنة'}
          />
          {errors.trackStock && <p className="text-xs text-danger">{errors.trackStock}</p>}

          {form.trackStock && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="حدّ التنبيه" type="number" min="0" ltr
                value={form.minStock} onChange={(e) => set('minStock', e.target.value)}
                error={errors.minStock}
                hint="ينبّهك النظام عند بلوغ هذا الرصيد"
              />
              {!isEdit && (
                <Input
                  label="الرصيد الافتتاحي" type="number" min="0" ltr
                  value={form.openingQty} onChange={(e) => set('openingQty', e.target.value)}
                  error={errors.openingQty}
                  hint="يُسجَّل كحركة مخزون، لا ككتابة مباشرة"
                />
              )}
            </div>
          )}

          {isEdit && form.trackStock && (
            <Alert tone="info">
              الرصيد الحالي <span className="font-semibold" dir="ltr">{product.quantity}</span> {product.unit}.
              لتعديله استعمل «تسوية المخزون» — لا يمكن تغيير الرصيد من هنا لأن كل تغيير يجب أن يُسجَّل كحركة.
            </Alert>
          )}
        </section>

        {/* ── تفاصيل الكتاب ── */}
        {isBook && (
          <section className="space-y-4 border-t border-border pt-5">
            <h3 className="text-sm font-semibold text-fg-muted">تفاصيل الكتاب</h3>
            {errors.bookDetail && <Alert tone="danger">{errors.bookDetail}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="ISBN" ltr
                value={form.isbn} onChange={(e) => set('isbn', e.target.value)}
                error={errors['bookDetail.isbn']}
              />
              <Select
                label="المؤلّف"
                value={form.authorId} onChange={(e) => set('authorId', e.target.value)}
                options={(authors ?? []).map((a) => ({ value: a.id, label: a.nameAr }))}
                placeholder="غير محدّد"
              />
              <Select
                label="الناشر"
                value={form.publisherId} onChange={(e) => set('publisherId', e.target.value)}
                options={(publishers ?? []).map((p) => ({ value: p.id, label: p.nameAr }))}
                placeholder="غير محدّد"
              />
              <Input
                label="عدد الصفحات" type="number" min="1" ltr
                value={form.pages} onChange={(e) => set('pages', e.target.value)}
                error={errors['bookDetail.pages']}
              />
              <Input
                label="اللغة"
                value={form.language} onChange={(e) => set('language', e.target.value)}
              />
              <Input
                label="سنة النشر" type="number" ltr
                value={form.publishYear} onChange={(e) => set('publishYear', e.target.value)}
                error={errors['bookDetail.publishYear']}
              />
            </div>
          </section>
        )}

        {/* ── الصور ── */}
        <section className="border-t border-border pt-5">
          <h3 className="mb-3 text-sm font-semibold text-fg">الصور</h3>
          <ImageUploader value={form.images} onChange={(images) => set('images', images)} />
        </section>

        {/* ── الحالة ── */}
        <section className="border-t border-border pt-5">
          <Switch
            checked={form.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
            label="منتج نشط"
            description="المنتجات غير النشطة لا تظهر في نقطة البيع ولا في تطبيق الزبائن"
          />
        </section>
      </form>
    </Dialog>
  )
}
